import React from 'react';

export interface StatSummaryCardStat {
  label: string;
  value: React.ReactNode;
  /** Optional override for the value's text color/weight, e.g. to highlight one stat. */
  valueClassName?: string;
}

export interface StatSummaryCardProps {
  /** Header title, shown on the highlighted top band. */
  title: React.ReactNode;
  /** Optional pill/tag rendered next to the title (e.g. a discount badge). */
  badge?: React.ReactNode;
  /**
   * Stat rows. Each row is rendered as an even-width strip of columns
   * separated by vertical dividers; any number of rows or columns per row
   * is supported — this is not limited to the 3-column layout it was first
   * built for.
   */
  rows: StatSummaryCardStat[][];
  className?: string;
}

/**
 * Bordered summary card: a highlighted title/badge band on top, followed by
 * one or more rows of labeled stats laid out in even columns. Introduced for
 * the "Applied Offer" preview on Create Subscription Plan (RR-1071, Figma
 * node 91835:606723) — there was no existing shared component for this
 * title-band + stat-grid pattern, so this is generic rather than tied to
 * that one use case.
 */
export const StatSummaryCard: React.FC<StatSummaryCardProps> = ({
  title,
  badge,
  rows,
  className = '',
}) => {
  return (
    <div
      className={`border border-information-90 rounded-[10px] overflow-hidden w-full ${className}`.trim()}
    >
      <div className="bg-information-90 flex items-center gap-2 px-4 py-2.5">
        <div className="min-w-0 flex-1 break-words text-titleSmall text-on-surface-variant">
          {title}
        </div>
        {badge != null && <div className="shrink-0">{badge}</div>}
      </div>
      <div className="bg-white flex flex-col">
        {rows.map((row, rowIndex) => (
          <div
            key={rowIndex}
            className={[
              'flex items-stretch',
              rowIndex > 0 ? 'border-t border-information-90' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {row.map((stat, colIndex) => (
              <div
                key={colIndex}
                className={[
                  'flex-1 min-w-0 px-4 py-3 flex flex-col gap-0.5',
                  colIndex < row.length - 1
                    ? 'border-r border-information-90'
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <p className="text-labelSmall text-neutral-10 opacity-50">
                  {stat.label}
                </p>
                <p
                  className={
                    stat.valueClassName ?? 'text-labelLarge text-neutral-10'
                  }
                >
                  {stat.value}
                </p>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default StatSummaryCard;
